import type { AppImage } from '@/lib/media/imageAssetFields'

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value)
const exactKeys = (value: Record<string, unknown>, keys: string[]) =>
  Object.keys(value).length === keys.length && keys.every(key => Object.hasOwn(value, key))
const boundedString = (value: unknown, max: number) =>
  typeof value === 'string' && new TextEncoder().encode(value).length <= max

// Setup avatars always use managed uploads. Ownership is checked by the save RPC.
export function validOpenWorldAvatar(value: unknown): value is AppImage | null {
  if (value === null) return true
  if (!isRecord(value) || !exactKeys(value, ['url', 'focus', 'asset'])) return false
  if (!boundedString(value.url, 4096) || !(value.url as string).startsWith('https://')) return false
  const focus = value.focus
  if (!isRecord(focus) || !exactKeys(focus, ['x', 'y'])
    || ![focus.x, focus.y].every(n => typeof n === 'number' && Number.isFinite(n) && n >= 0 && n <= 100)) return false
  const asset = value.asset
  if (!isRecord(asset) || !exactKeys(asset, ['provider', 'bucket', 'path', 'public_url', 'mime_type', 'bytes', 'width', 'height', 'original_name'])) return false
  return asset.provider === 'supabase-storage' && asset.bucket === 'public-media'
    && typeof asset.path === 'string' && /^open-world-setups\/[0-9a-f-]{36}\/[0-9a-f-]{36}\.(webp|png|jpg|bmp)$/u.test(asset.path)
    && asset.public_url === value.url
    && ['image/webp', 'image/png', 'image/jpeg', 'image/bmp'].includes(asset.mime_type as string)
    && boundedString(asset.original_name, 1024)
    && [asset.bytes, asset.width, asset.height].every(n => n === null || (Number.isSafeInteger(n) && (n as number) > 0 && (n as number) <= 2_147_483_647))
}
